import { useState } from "react";
import { useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { QRCodeSVG } from "qrcode.react";
import { Printer } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { PageHeader } from "@/components/AppShell";
import { PathTrail } from "@/components/PathTrail";
import { UpgradeHint } from "@/components/UpgradeHint";
import { api, errDetail } from "@/lib/api";
import { useI18n } from "@/i18n/I18n";

export default function LabelsPage() {
  const { id } = useParams();
  const { t } = useI18n();
  const [onlyBoxes, setOnlyBoxes] = useState(false);
  const { data = [], isLoading, error } = useQuery({
    queryKey: ["location", id, "labels"], retry: false,
    queryFn: () => api.post(`/locations/${id}/qr-bulk`).then((r) => r.data),
  });
  if (isLoading) return <p className="text-slate-500">{t("common.loading")}</p>;
  if (error) return <UpgradeHint text={t(errDetail(error) === "feature_qr" ? "loc.qrLocked" : "common.error")} testId="labels-locked" />;
  const root = data.find((l) => l.id === id);
  const labels = onlyBoxes ? data.filter((l) => l.kind === "box") : data;

  return (
    <div>
      <PageHeader title={t("labels.title")} subtitle={t("labels.subtitle", { count: labels.length, name: root?.name || "" })} testId="labels-title"
        actions={<Button data-testid="labels-print-button" className="h-11 gap-2" onClick={() => window.print()}><Printer className="w-4 h-4" />{t("labels.print")}</Button>} />
      {root && <div className="mb-6"><PathTrail path={root.path} /></div>}
      <label className="flex items-center gap-3 mb-6 text-sm font-medium"><Switch data-testid="labels-only-boxes" checked={onlyBoxes} onCheckedChange={setOnlyBoxes} />{t("labels.onlyBoxes")}</label>
      <div className="print-sheet grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 print:grid-cols-3 print:gap-2" data-testid="labels-sheet">
        {labels.map((l) => (
          <div key={l.id} data-testid={`label-${l.id}`} className="break-inside-avoid rounded-xl border-2 border-slate-900 bg-white p-3 text-center space-y-1.5">
            <QRCodeSVG value={`${window.location.origin}/l/${l.id}`} size={128} level="M" className="mx-auto" />
            <p className="font-head font-extrabold text-base break-words leading-tight">{l.name}</p>
            <p className="font-mono text-[9px] text-slate-500 break-words">{l.path.map((p) => p.name).join(" → ")}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
